import { useMemo } from "react";
import SEO from "@/components/SEO";
import { useSettings } from "@/context/SettingsContext";
import Breadcrumbs, { buildBreadcrumbSchema } from "@/components/Breadcrumbs";
import Search from "@/pages/Search";

const META = {
  tour: {
    slug: "/tours",
    category: "tour",
    label: "Tours",
    h1: "Hyderabad Tours & Getaways",
    intro: "Curated day tours, weekend getaways and multi-day trips from Hyderabad — SIC, private & group departures.",
    title: "Hyderabad Tours & Weekend Getaways — Book Online",
    description: "Book tours from Hyderabad: day trips, weekend getaways, Kashmir, Goa, Kerala, Rajasthan. GST-invoice, Razorpay-secured. Since 2005.",
    keywords: "hyderabad tours, tour packages from hyderabad, weekend getaways hyderabad, kashmir tour hyderabad",
  },
  hotel: {
    slug: "/hotels",
    category: "hotel",
    label: "Hotels & Staycations",
    h1: "Hotels & Staycations near Hyderabad",
    intro: "Verified hotels, resorts and boutique staycations — forest, lake and heritage escapes within 200 km.",
    title: "Hotels & Staycations near Hyderabad — Book Online",
    description: "Book hotels and staycations near Hyderabad. Forest, lake & heritage escapes under ₹5,000. GST invoice, instant confirmation.",
    keywords: "hotels hyderabad, staycations near hyderabad, resorts hyderabad, weekend hotels",
  },
  ticket: {
    slug: "/tickets",
    category: "ticket",
    label: "Entry Tickets",
    h1: "Attraction Entry Tickets in Hyderabad",
    intro: "Skip-the-queue tickets to Ramoji Film City, Wonderla, Snow World, Nehru Zoo, Salar Jung Museum and more.",
    title: "Ramoji Film City, Wonderla & Attraction Tickets — Hyderabad",
    description: "Book Ramoji Film City tickets, Wonderla, Snow World, Nehru Zoo entry tickets online. Skip queues, GST invoice, instant vouchers.",
    keywords: "ramoji film city tickets, wonderla hyderabad tickets, snow world tickets, entry tickets hyderabad",
  },
  cab: {
    slug: "/cabs",
    category: "cab",
    label: "Cabs & Transport",
    h1: "Cabs, Airport Transfers & Outstation from Hyderabad",
    intro: "Airport pickups, outstation sedan / SUV / 7-seater, Hyderabad local rentals. GST-billed & tracked.",
    title: "Hyderabad Airport Cabs & Outstation Taxi Booking",
    description: "Book Hyderabad airport cabs, outstation sedan/SUV/tempo/7-seater, city rentals. Transparent pricing, GST invoice, live tracking.",
    keywords: "hyderabad airport cab, outstation taxi hyderabad, hyderabad car rental, suv hire hyderabad",
  },
  activity: {
    slug: "/adventure",
    category: "activity",
    label: "Adventure & Camping",
    h1: "Adventure & Camping near Hyderabad",
    intro: "Trekking, camping, water sports and outdoor experiences within a 3-hour drive of Hyderabad.",
    title: "Adventure, Trekking & Camping near Hyderabad",
    description: "Book adventure trips near Hyderabad: trekking, lake camping, water sports, ATV rides. All-inclusive, GST-billed since 2005.",
    keywords: "adventure hyderabad, trekking near hyderabad, camping near hyderabad, water sports hyderabad",
  },
  international: {
    slug: "/international",
    category: "international",
    label: "International Tours",
    h1: "International Tours from Hyderabad",
    intro: "Escorted group tours & customised holidays to Dubai, Singapore-Malaysia, Thailand, Bali, Europe.",
    title: "International Tours & Holiday Packages from Hyderabad",
    description: "Book international tours from Hyderabad — Dubai, Singapore, Thailand, Bali, Europe. All-inclusive packages, Razorpay-secured.",
    keywords: "international tours from hyderabad, dubai tour hyderabad, singapore tour hyderabad, thailand hyderabad",
  },
};

export default function CategoryLanding({ categoryKey }) {
  const meta = META[categoryKey];
  const { settings } = useSettings();
  const siteName = settings.site_name || "HyderabadTourism.in";
  const siteUrl = settings.site_url || (typeof window !== "undefined" ? window.location.origin : "");
  const breadcrumbItems = useMemo(() => ([
    { label: "Home", to: "/" },
    { label: meta.label },
  ]), [meta]);

  const canonical = typeof window !== "undefined" ? `${window.location.origin}${meta.slug}` : `${siteUrl}${meta.slug}`;

  return (
    <div data-testid={`category-landing-${meta.category}`}>
      <SEO
        title={meta.title}
        description={meta.description}
        url={canonical}
        schemas={[
          buildBreadcrumbSchema(breadcrumbItems),
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": meta.h1,
            "description": meta.description,
            "url": canonical,
            "isPartOf": {
              "@type": "WebSite",
              "name": siteName,
              "url": typeof window !== "undefined" ? window.location.origin : siteUrl,
            },
          },
        ]}
      />

      <section className="bg-gradient-to-b from-secondary/40 to-background pt-8 pb-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={breadcrumbItems} />
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mb-2">{meta.h1}</h1>
          <p className="text-muted-foreground max-w-3xl">{meta.intro}</p>
        </div>
      </section>

      {/* Reuse the full Search UI, preset with this category */}
      <Search presetCategory={meta.category} hideHeader />
    </div>
  );
}
